import {
  ArrowLeftIcon,
  CheckCircleIcon,
  LightningBoltIcon,
  PhotographIcon,
  SparklesIcon,
  UserIcon,
  VolumeUpIcon,
  XIcon,
} from '@heroicons/react/outline';
import { PlayIcon, PauseIcon } from '@heroicons/react/solid';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import React, { FC, useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Voice } from 'types';

import clsxm from '@/lib/clsxm';
import uuidv4 from '@/lib/uuid';

import Button from '@/components/buttons/Button';
import Spinner from '@/components/Spinner';
import ActorPhotoTransform from '@/components/videos/ActorPhotoTransform';
import ActorPhotoGenerator from '@/components/videos/ActorPhotoGenerator';

import { createClient } from '@/utils/supabase/component';
import axiosInstance from '@/utils/httpClient';
import { getVoiceImageUrl } from '@/utils/voices';

import type { CustomActor } from '@/types/actor';

interface CreateActorModalProps {
  voices: Voice[];
  onClose: () => void;
  onActorCreated: (actor: CustomActor) => void;
  canCreate: boolean;
  maxActors: number;
  currentCount: number;
}

type Step = 'photo' | 'voice' | 'details' | 'preview';

const STEPS: Step[] = ['photo', 'voice', 'details', 'preview'];

export const CreateActorModal: FC<CreateActorModalProps> = ({
  voices,
  onClose,
  onActorCreated,
  canCreate,
  maxActors,
  currentCount,
}) => {
  const { t } = useLocale('ai-talking-videos');
  const supabaseClient = createClient();

  // Step state
  const [currentStep, setCurrentStep] = useState<Step>('photo');

  // Form data
  const [photo, setPhoto] = useState<string>('');
  const [originalPhoto, setOriginalPhoto] = useState<string>(''); // Keep track of original for transforms
  const [selectedVoice, setSelectedVoice] = useState<Voice | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // UI state
  const [isUploading, setIsUploading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isPlayingVoice, setIsPlayingVoice] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [voiceSearch, setVoiceSearch] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [showTransform, setShowTransform] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [wasTransformed, setWasTransformed] = useState(false);
  const [wasGenerated, setWasGenerated] = useState(false);
  const [photoAspectRatio, setPhotoAspectRatio] = useState<string>('1:1');

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentStepIndex = STEPS.indexOf(currentStep);

  // Filter voices based on search
  const filteredVoices = voices.filter(
    (voice) =>
      voice.name.toLowerCase().includes(voiceSearch.toLowerCase()) ||
      voice.accent?.toLowerCase().includes(voiceSearch.toLowerCase()) ||
      (Array.isArray(voice.language)
        ? voice.language.some((l) =>
            l.toLowerCase().includes(voiceSearch.toLowerCase()),
          )
        : voice.language?.toLowerCase().includes(voiceSearch.toLowerCase())),
  );

  // Handle file upload
  const handleFileUpload = useCallback(
    async (file: File) => {
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(t('Please upload an image file'));
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('Image must be less than 10MB'));
        return;
      }

      setIsUploading(true);
      let objectUrl = '';

      try {
        // Validate dimensions
        const img = new window.Image();
        objectUrl = URL.createObjectURL(file);
        img.src = objectUrl;

        const imageDimensions = await new Promise<{
          width: number;
          height: number;
        }>((resolve, reject) => {
          img.onload = () => {
            if (img.width < 512 || img.height < 512) {
              reject(
                new Error(
                  t('Image must be at least 512x512 pixels for best quality'),
                ),
              );
            }
            resolve({ width: img.width, height: img.height });
          };
          img.onerror = () => reject(new Error(t('Failed to load image')));
        });

        // Upload to Supabase
        const fileExt = file.name.split('.').pop();
        const fileName = `${uuidv4()}.${fileExt}`;
        const filePath = `custom-actors/${fileName}`;

        const { error: uploadError } = await supabaseClient.storage
          .from('uploads')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: fileData } = supabaseClient.storage
          .from('uploads')
          .getPublicUrl(filePath);

        if (fileData?.publicUrl) {
          setPhoto(fileData.publicUrl);
          setOriginalPhoto(fileData.publicUrl);
          setWasTransformed(false);
          // Calculate and set aspect ratio from uploaded image dimensions
          const ratio = imageDimensions.width / imageDimensions.height;
          // Find closest standard aspect ratio
          const standardRatios = [
            { name: '1:1', value: 1 },
            { name: '4:3', value: 4 / 3 },
            { name: '3:4', value: 3 / 4 },
            { name: '16:9', value: 16 / 9 },
            { name: '9:16', value: 9 / 16 },
            { name: '4:5', value: 4 / 5 },
          ];
          const closest = standardRatios.reduce((prev, curr) =>
            Math.abs(curr.value - ratio) < Math.abs(prev.value - ratio)
              ? curr
              : prev,
          );
          setPhotoAspectRatio(closest.name);
          // Don't auto-advance - let user choose to transform or continue
        }
      } catch (error: any) {
        toast.error(error.message || t('Failed to upload image'));
      } finally {
        setIsUploading(false);
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      }
    },
    [supabaseClient, t],
  );

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFileUpload(file);
    },
    [handleFileUpload],
  );

  // Voice preview
  const playVoicePreview = useCallback(
    (voice: Voice) => {
      if (!voice.preview_url) return;

      if (isPlayingVoice && playingVoiceId === voice.voice_id) {
        audioRef.current?.pause();
        setIsPlayingVoice(false);
        setPlayingVoiceId(null);
        return;
      }

      if (audioRef.current) {
        audioRef.current.pause();
      }

      const audio = new Audio(voice.preview_url);
      audioRef.current = audio;
      audio.play().catch(() => {});
      setIsPlayingVoice(true);
      setPlayingVoiceId(voice.voice_id);
      audio.onended = () => {
        setIsPlayingVoice(false);
        setPlayingVoiceId(null);
      };
    },
    [isPlayingVoice, playingVoiceId],
  );

  // Create actor
  const handleCreate = async () => {
    if (!photo || !selectedVoice || !name.trim()) {
      toast.error(t('Please complete all required fields'));
      return;
    }

    setIsCreating(true);

    try {
      const response = await axiosInstance.post('/api/custom-actors', {
        name: name.trim(),
        photo,
        voice_id: selectedVoice.voice_id,
        description: description.trim(),
      });

      if (response.data.actor) {
        toast.success(t('Actor created successfully!'));
        onActorCreated(response.data.actor);
        onClose();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('Failed to create actor'));
    } finally {
      setIsCreating(false);
    }
  };

  // Navigation
  const goBack = () => {
    // If showing transform, go back to photo view
    if (showTransform) {
      setShowTransform(false);
      return;
    }
    // If showing generator, go back to photo view
    if (showGenerator) {
      setShowGenerator(false);
      return;
    }
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex]);
    }
  };

  const goNext = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < STEPS.length) {
      setCurrentStep(STEPS[nextIndex]);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 'photo':
        return !!photo;
      case 'voice':
        return !!selectedVoice;
      case 'details':
        return !!name.trim();
      case 'preview':
        return true;
      default:
        return false;
    }
  };

  // Upgrade prompt for users who can't create actors
  if (!canCreate) {
    return (
      <div className='px-6 py-10 text-center'>
        <div className='mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30'>
          <SparklesIcon className='h-8 w-8 text-indigo-600 dark:text-indigo-400' />
        </div>
        <h2 className='mb-2 text-2xl font-bold text-gray-900 dark:text-white'>
          {currentCount >= maxActors
            ? t('Actor Limit Reached')
            : t('Upgrade to Create Actors')}
        </h2>
        <p className='mb-8 text-gray-600 dark:text-gray-400'>
          {currentCount >= maxActors
            ? t(
                "You've created {{currentCount}} of {{maxActors}} custom actors. Upgrade your plan for more.",
                { currentCount, maxActors },
              )
            : t(
                'Custom actors are available on paid plans. Upgrade to create your own AI actors.',
              )}
        </p>
        <div className='flex justify-center gap-3'>
          <Button variant='outline' onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant='dark'
            onClick={() => (window.location.href = '/settings/billing')}
          >
            {t('Upgrade Plan')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col max-h-[85vh]'>
      {/* Header */}
      <div className='flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700'>
        <div className='flex items-center gap-3'>
          {(currentStepIndex > 0 || showTransform || showGenerator) && (
            <button
              onClick={goBack}
              className='rounded-full p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
            >
              <ArrowLeftIcon className='h-5 w-5 text-gray-600 dark:text-gray-400' />
            </button>
          )}
          <div>
            <h2 className='text-lg font-semibold text-gray-900 dark:text-white'>
              {showTransform
                ? t('Transform Photo')
                : showGenerator
                  ? t('Generate Actor')
                  : t('Create Your Actor')}
            </h2>
            <p className='text-sm text-gray-500 dark:text-gray-400'>
              {showTransform
                ? t('AI-powered photo enhancement')
                : showGenerator
                  ? t('Create an AI-generated actor')
                  : t('{{currentCount}} / {{maxActors}} actors used', {
                      currentCount,
                      maxActors,
                    })}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className='rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
        >
          <XIcon className='h-5 w-5 text-gray-500' />
        </button>
      </div>

      {/* Progress indicator - hidden during transform/generator */}
      {!showTransform && !showGenerator && (
        <div className='px-6 py-4 border-b border-gray-100 dark:border-gray-800'>
          <div className='flex items-center justify-between'>
            {STEPS.map((step, index) => (
              <React.Fragment key={step}>
                <div className='flex flex-col items-center'>
                  <div
                    className={clsxm(
                      'flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300',
                      index < currentStepIndex
                        ? 'bg-green-500 text-white'
                        : index === currentStepIndex
                          ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30'
                          : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
                    )}
                  >
                    {index < currentStepIndex ? (
                      <CheckCircleIcon className='h-6 w-6' />
                    ) : step === 'photo' ? (
                      <PhotographIcon className='h-5 w-5' />
                    ) : step === 'voice' ? (
                      <VolumeUpIcon className='h-5 w-5' />
                    ) : step === 'details' ? (
                      <UserIcon className='h-5 w-5' />
                    ) : (
                      <SparklesIcon className='h-5 w-5' />
                    )}
                  </div>
                  <span
                    className={clsxm(
                      'mt-2 text-xs font-medium',
                      index === currentStepIndex
                        ? 'text-indigo-600 dark:text-indigo-400'
                        : 'text-gray-500 dark:text-gray-400',
                    )}
                  >
                    {step === 'photo'
                      ? t('Photo')
                      : step === 'voice'
                        ? t('Voice')
                        : step === 'details'
                          ? t('Details')
                          : t('Preview')}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={clsxm(
                      'h-0.5 flex-1 mx-2 transition-all duration-300',
                      index < currentStepIndex
                        ? 'bg-green-500'
                        : 'bg-gray-200 dark:bg-gray-700',
                    )}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className='flex-1 overflow-y-auto px-6 py-6'>
        {/* Transform Mode */}
        {showTransform && photo && (
          <ActorPhotoTransform
            originalPhoto={originalPhoto || photo}
            onTransformComplete={(newPhotoUrl, aspectRatio) => {
              setPhoto(newPhotoUrl);
              setPhotoAspectRatio(aspectRatio);
              setWasTransformed(true);
              setShowTransform(false);
              // Auto-advance to voice step after transform
              setTimeout(() => setCurrentStep('voice'), 300);
            }}
            onCancel={() => setShowTransform(false)}
            onUseOriginal={() => {
              setPhoto(originalPhoto || photo);
              setWasTransformed(false);
              setShowTransform(false);
              // Auto-advance to voice step
              setTimeout(() => setCurrentStep('voice'), 300);
            }}
          />
        )}

        {/* Generator Mode */}
        {showGenerator && (
          <ActorPhotoGenerator
            onGenerateComplete={(newPhotoUrl, aspectRatio) => {
              setPhoto(newPhotoUrl);
              setOriginalPhoto(newPhotoUrl);
              setPhotoAspectRatio(aspectRatio);
              setWasGenerated(true);
              setWasTransformed(false);
              setShowGenerator(false);
              // Auto-advance to voice step after generation
              setTimeout(() => setCurrentStep('voice'), 300);
            }}
            onCancel={() => setShowGenerator(false)}
          />
        )}

        {/* Step 1: Photo Upload */}
        {currentStep === 'photo' && !showTransform && !showGenerator && (
          <div className='space-y-6'>
            <div className='text-center'>
              <h3 className='text-xl font-semibold text-gray-900 dark:text-white'>
                {t('Upload Actor Photo')}
              </h3>
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                {t('Choose a clear photo of a face for best results')}
              </p>
            </div>

            {!photo ? (
              <>
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={clsxm(
                    'relative cursor-pointer rounded-2xl border-2 border-dashed p-12 text-center transition-all duration-200',
                    dragOver
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50 dark:border-gray-600 dark:hover:border-indigo-500 dark:hover:bg-gray-800/50',
                  )}
                >
                  {isUploading ? (
                    <div className='flex flex-col items-center'>
                      <Spinner className='h-12 w-12 animate-spin text-indigo-500' />
                      <p className='mt-4 text-sm text-gray-600 dark:text-gray-400'>
                        {t('Uploading...')}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/30'>
                        <PhotographIcon className='h-8 w-8 text-indigo-600 dark:text-indigo-400' />
                      </div>
                      <p className='text-lg font-medium text-gray-900 dark:text-white'>
                        {t('Drag and drop your photo here')}
                      </p>
                      <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                        {t('or click to browse')}
                      </p>
                      <p className='mt-4 text-xs text-gray-400 dark:text-gray-500'>
                        {t('PNG, JPG, or WEBP. Minimum 512x512 pixels.')}
                      </p>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type='file'
                    className='hidden'
                    accept='image/png,image/jpeg,image/jpg,image/webp'
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file);
                    }}
                  />
                </div>

                {/* Generate with AI Option */}
                <div className='relative flex items-center'>
                  <div className='flex-grow border-t border-gray-200 dark:border-gray-700' />
                  <span className='mx-4 text-sm text-gray-400 dark:text-gray-500'>
                    {t('or')}
                  </span>
                  <div className='flex-grow border-t border-gray-200 dark:border-gray-700' />
                </div>

                <button
                  onClick={() => setShowGenerator(true)}
                  className='group relative w-full overflow-hidden rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50 p-5 text-left transition-all duration-300 hover:border-indigo-400 hover:shadow-lg hover:shadow-indigo-500/10 dark:border-indigo-800 dark:from-indigo-900/20 dark:to-purple-900/20 dark:hover:border-indigo-600'
                >
                  <div className='absolute inset-0 bg-gradient-to-r from-indigo-500/0 via-indigo-500/5 to-purple-500/0 opacity-0 transition-opacity duration-500 group-hover:opacity-100' />

                  <div className='relative flex items-center gap-4'>
                    <div className='flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/25 transition-transform duration-300 group-hover:scale-110'>
                      <SparklesIcon className='h-6 w-6 text-white' />
                    </div>
                    <div className='flex-1'>
                      <div className='flex items-center gap-2'>
                        <h4 className='font-semibold text-gray-900 dark:text-white'>
                          {t('Generate with AI')}
                        </h4>
                        <span className='rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'>
                          {t('NEW')}
                        </span>
                      </div>
                      <p className='mt-0.5 text-sm text-gray-600 dark:text-gray-400'>
                        {t('Create an AI-generated actor portrait')}
                      </p>
                    </div>
                    <div className='flex-shrink-0 text-indigo-500 transition-transform duration-300 group-hover:translate-x-1'>
                      <svg
                        className='h-5 w-5'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M9 5l7 7-7 7'
                        />
                      </svg>
                    </div>
                  </div>
                </button>
              </>
            ) : (
              <div className='flex flex-col items-center space-y-6'>
                {/* Photo Preview */}
                <div className='relative'>
                  <div className='h-48 w-48 overflow-hidden rounded-2xl shadow-xl ring-4 ring-white dark:ring-gray-800'>
                    <Image
                      src={photo}
                      alt='Actor photo'
                      width={384}
                      height={384}
                      quality={95}
                      className='h-full w-full object-cover'
                    />
                  </div>
                  <button
                    onClick={() => {
                      setPhoto('');
                      setOriginalPhoto('');
                      setWasTransformed(false);
                      setWasGenerated(false);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className='absolute -right-2 -top-2 rounded-full bg-red-500 p-1.5 text-white shadow-lg hover:bg-red-600 transition-colors'
                  >
                    <XIcon className='h-4 w-4' />
                  </button>
                  {wasTransformed && (
                    <div className='absolute -left-2 -top-2 flex items-center gap-1 rounded-full bg-purple-500 px-2 py-1 text-xs font-medium text-white shadow-lg'>
                      <SparklesIcon className='h-3 w-3' />
                      {t('AI Enhanced')}
                    </div>
                  )}
                  {wasGenerated && !wasTransformed && (
                    <div className='absolute -left-2 -top-2 flex items-center gap-1 rounded-full bg-indigo-500 px-2 py-1 text-xs font-medium text-white shadow-lg'>
                      <SparklesIcon className='h-3 w-3' />
                      {t('AI Generated')}
                    </div>
                  )}
                </div>

                {/* AI Transform Option - Premium Card */}
                <div className='w-full max-w-md'>
                  <button
                    onClick={() => setShowTransform(true)}
                    className='group relative w-full overflow-hidden rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 p-5 text-left transition-all duration-300 hover:border-purple-400 hover:shadow-lg hover:shadow-purple-500/10 dark:border-purple-800 dark:from-purple-900/20 dark:to-indigo-900/20 dark:hover:border-purple-600'
                  >
                    {/* Animated gradient background */}
                    <div className='absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/5 to-indigo-500/0 opacity-0 transition-opacity duration-500 group-hover:opacity-100' />

                    <div className='relative flex items-center gap-4'>
                      <div className='flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg shadow-purple-500/25 transition-transform duration-300 group-hover:scale-110'>
                        <LightningBoltIcon className='h-6 w-6 text-white' />
                      </div>
                      <div className='flex-1'>
                        <div className='flex items-center gap-2'>
                          <h4 className='font-semibold text-gray-900 dark:text-white'>
                            {t('Transform with AI')}
                          </h4>
                          <span className='rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'>
                            {t('NEW')}
                          </span>
                        </div>
                        <p className='mt-0.5 text-sm text-gray-600 dark:text-gray-400'>
                          {t(
                            'Make yourself a news anchor, influencer, executive, and more',
                          )}
                        </p>
                      </div>
                      <div className='flex-shrink-0 text-purple-500 transition-transform duration-300 group-hover:translate-x-1'>
                        <svg
                          className='h-5 w-5'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M9 5l7 7-7 7'
                          />
                        </svg>
                      </div>
                    </div>
                  </button>
                </div>

                {/* Change photo link */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className='text-sm font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400'
                >
                  {t('Choose different photo')}
                </button>
                <input
                  ref={fileInputRef}
                  type='file'
                  className='hidden'
                  accept='image/png,image/jpeg,image/jpg,image/webp'
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file);
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Step 2: Voice Selection */}
        {currentStep === 'voice' && !showTransform && (
          <div className='space-y-4'>
            <div className='text-center'>
              <h3 className='text-xl font-semibold text-gray-900 dark:text-white'>
                {t('Choose a Voice')}
              </h3>
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                {t('Select the voice your actor will use')}
              </p>
            </div>

            {/* Search */}
            <div className='relative'>
              <input
                type='text'
                placeholder={t('Search voices by name, language, or accent...')}
                value={voiceSearch}
                onChange={(e) => setVoiceSearch(e.target.value)}
                className='w-full rounded-xl border border-gray-300 bg-white px-4 py-3 pl-11 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white'
              />
              <VolumeUpIcon className='absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400' />
            </div>

            {/* Voice grid */}
            <div className='grid max-h-[40vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3'>
              {filteredVoices.map((voice) => {
                const isSelected = selectedVoice?.voice_id === voice.voice_id;
                const isPlaying =
                  isPlayingVoice && playingVoiceId === voice.voice_id;

                return (
                  <div
                    key={voice.voice_id}
                    onClick={() => setSelectedVoice(voice)}
                    className={clsxm(
                      'group relative cursor-pointer rounded-xl border-2 p-3 transition-all duration-200',
                      isSelected
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-gray-200 hover:border-indigo-300 dark:border-gray-700 dark:hover:border-indigo-600',
                    )}
                  >
                    <div className='flex items-center gap-3'>
                      <div className='relative'>
                        <Image
                          src={getVoiceImageUrl(voice.image)}
                          alt={voice.name}
                          width={48}
                          height={48}
                          className='rounded-lg object-cover'
                        />
                        {voice.preview_url && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              playVoicePreview(voice);
                            }}
                            className='absolute inset-0 flex items-center justify-center rounded-lg bg-black/40 opacity-0 transition-opacity group-hover:opacity-100'
                          >
                            {isPlaying ? (
                              <PauseIcon className='h-5 w-5 text-white' />
                            ) : (
                              <PlayIcon className='h-5 w-5 text-white' />
                            )}
                          </button>
                        )}
                      </div>
                      <div className='flex-1 min-w-0'>
                        <p className='truncate text-sm font-medium text-gray-900 dark:text-white'>
                          {voice.name}
                        </p>
                        <p className='truncate text-xs text-gray-500 dark:text-gray-400'>
                          {Array.isArray(voice.language)
                            ? voice.language[0]
                            : voice.language}
                          {voice.accent && ` - ${voice.accent}`}
                        </p>
                      </div>
                    </div>
                    {isSelected && (
                      <div className='absolute -right-1 -top-1'>
                        <CheckCircleIcon className='h-6 w-6 text-indigo-500' />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 3: Actor Details */}
        {currentStep === 'details' && !showTransform && (
          <div className='space-y-6'>
            <div className='text-center'>
              <h3 className='text-xl font-semibold text-gray-900 dark:text-white'>
                {t('Name Your Actor')}
              </h3>
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                {t('Give your actor a memorable name and description')}
              </p>
            </div>

            <div className='mx-auto max-w-md space-y-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  {t('Actor Name')} *
                </label>
                <input
                  type='text'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('e.g., "Marketing Manager Sarah"')}
                  maxLength={50}
                  className='mt-1 w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white'
                />
                <p className='mt-1 text-right text-xs text-gray-400'>
                  {name.length}/50
                </p>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  {t('Description')} ({t('optional')})
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t(
                    'e.g., "Professional presenter for product demos"',
                  )}
                  maxLength={200}
                  rows={3}
                  className='mt-1 w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white'
                />
                <p className='mt-1 text-right text-xs text-gray-400'>
                  {description.length}/200
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Preview */}
        {currentStep === 'preview' && !showTransform && (
          <div className='space-y-6'>
            <div className='text-center'>
              <h3 className='text-xl font-semibold text-gray-900 dark:text-white'>
                {t('Preview Your Actor')}
              </h3>
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                {t("Here's how your actor will appear")}
              </p>
            </div>

            <div className='mx-auto max-w-sm'>
              <div className='overflow-hidden rounded-2xl border-2 border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800'>
                <div
                  className='w-full'
                  style={{
                    aspectRatio: photoAspectRatio.replace(':', '/'),
                  }}
                >
                  <Image
                    src={photo}
                    alt={name}
                    width={600}
                    height={600}
                    quality={100}
                    className='h-full w-full object-cover'
                  />
                </div>
                <div className='p-4'>
                  <h4 className='text-lg font-semibold text-gray-900 dark:text-white'>
                    {name}
                  </h4>
                  {description && (
                    <p className='mt-1 text-sm text-gray-600 dark:text-gray-400'>
                      {description}
                    </p>
                  )}
                  <div className='mt-3 flex items-center gap-2'>
                    <div className='h-8 w-8 overflow-hidden rounded-full'>
                      <Image
                        src={getVoiceImageUrl(selectedVoice?.image)}
                        alt={selectedVoice?.name || ''}
                        width={32}
                        height={32}
                        className='h-full w-full object-cover'
                      />
                    </div>
                    <div className='flex-1'>
                      <p className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                        {selectedVoice?.name}
                      </p>
                      <p className='text-xs text-gray-500'>
                        {Array.isArray(selectedVoice?.language)
                          ? selectedVoice?.language[0]
                          : selectedVoice?.language}
                      </p>
                    </div>
                    {selectedVoice?.preview_url && (
                      <button
                        onClick={() =>
                          selectedVoice && playVoicePreview(selectedVoice)
                        }
                        className='rounded-full bg-indigo-100 p-2 text-indigo-600 hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400'
                      >
                        {isPlayingVoice &&
                        playingVoiceId === selectedVoice?.voice_id ? (
                          <PauseIcon className='h-4 w-4' />
                        ) : (
                          <PlayIcon className='h-4 w-4' />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer - hidden during transform/generator mode (they have their own buttons) */}
      {!showTransform && !showGenerator && (
        <div className='border-t border-gray-200 px-6 py-4 dark:border-gray-700'>
          <div className='flex justify-end'>
            {currentStep === 'preview' ? (
              <Button
                variant='dark'
                onClick={handleCreate}
                isLoading={isCreating}
                disabled={isCreating}
                className='bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
              >
                <SparklesIcon className='mr-2 h-4 w-4' />
                {t('Create Actor')}
              </Button>
            ) : (
              <Button
                variant='dark'
                onClick={goNext}
                disabled={!canProceed()}
                className='bg-indigo-600 hover:bg-indigo-700'
              >
                {t('Continue')}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Hidden audio element for voice preview */}
      <audio ref={audioRef} className='hidden' />
    </div>
  );
};

export default CreateActorModal;
